import React, { useEffect, useRef, useState } from 'react';

/**
 * IB-755 — Budget Versions v2.
 *
 * A small inline SVG chart of net position across ALL versions (locked
 * snapshots + the live Latest Estimate), ordered by version. `trend` is an
 * array of { id, version, net }.
 *
 * When a specific version is chosen in the Compare dropdown, `baselineNet` is
 * that version's net: it is drawn as a dashed horizontal "baseline ref" line
 * and every point is coloured green (net at/above the baseline — a stronger or
 * equal position) or red (below it). In the default "Previous version" mode
 * `baselineNet` is null: no baseline line, and points render in the line
 * colour. Renders nothing with no data.
 */
const LINE = '#055DF5';
const GREEN = '#00DD8D';
const RED = '#F00024';
const BASELINE = '#B4B9C4';

export default function TrendSparkline({ trend = [], baselineNet = null, height = 44 }) {
    // Measure the container so the chart spans the FULL tile width (crisp points,
    // no aspect-ratio stretching). Starts at a sane fallback; a ResizeObserver
    // updates it once the tile is laid out — including the hidden-tab → visible
    // transition, where the initial width is 0.
    const containerRef = useRef(null);
    const [width, setWidth] = useState(280);

    useEffect(() => {
        const el = containerRef.current;
        if (!el || typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const update = () => {
            const w = el.clientWidth;
            if (w > 0) {
                setWidth(w);
            }
        };

        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);

        return () => observer.disconnect();
    }, []);

    if (!Array.isArray(trend) || trend.length === 0) {
        return null;
    }

    const hasBaseline = typeof baselineNet === 'number';
    const nets = trend.map((point) => Number(point.net) || 0);

    // Include the baseline in the vertical range so its line is always in view
    // (it normally coincides with one of the points, but guard regardless).
    const scaleValues = hasBaseline ? [...nets, baselineNet] : nets;
    const min = Math.min(...scaleValues);
    const max = Math.max(...scaleValues);
    const range = max - min || 1;

    const stepX = trend.length > 1 ? width / (trend.length - 1) : 0;
    // Invert Y so higher net sits higher on screen; pad 4px top/bottom.
    const yOf = (net) => height - 4 - ((net - min) / range) * (height - 8);

    const points = nets.map((net, index) => {
        const x = trend.length > 1 ? index * stepX : width / 2;
        return [x, yOf(net)];
    });

    const polyline = points.map(([x, y]) => `${x},${y}`).join(' ');
    const baselineY = hasBaseline ? yOf(baselineNet) : null;

    const pointFill = (net) => {
        if (!hasBaseline) {
            return LINE;
        }

        return net >= baselineNet ? GREEN : RED;
    };

    return (
        <div ref={containerRef} className="tw-w-full">
            <svg
                width={width}
                height={height}
                viewBox={`0 0 ${width} ${height}`}
                className="tw-overflow-visible"
                role="img"
                aria-label={$t('Net position across versions')}
            >
                {hasBaseline && <line x1="0" y1={baselineY} x2={width} y2={baselineY} stroke={BASELINE} strokeWidth="1.5" strokeDasharray="4 3" />}
                {points.length > 1 && <polyline points={polyline} fill="none" stroke={LINE} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />}
                {points.map(([x, y], index) => (
                    <circle key={trend[index].id ?? index} cx={x} cy={y} r="3" fill={pointFill(nets[index])} />
                ))}
            </svg>
        </div>
    );
}
